/*
 * Formulario de cotización.
 *
 * Contiene los subcomponentes necesarios para la creación y edición de una cotización. Entre éstos
 * se encuentran la información del cliente, datos de facturación y de envío; listas de productos y
 * de circuitos así como el resumen de pago.
 *
 * Created on Tue Jun 13 2023 by José Luis Chávez Ayala.
 *
 * Copyright (c) 2023 Turn My App.
 */

<template>
  <div>
    <validation-observer ref="quoteForm">

      <!-- #region::Form -->
      <b-form @submit="$event.preventDefault()">

        <!-- #region::Card for main options -->
        <div class="mb-2">
          <MainOptions
            :title="id ? 'Actualizar cotización' : 'Nueva cotización'"
            :save-button-text="id ? 'Guardar cambios' : 'Guardar cotización'"
            model-home-route="quotes-home"
            :callback="saveQuote"
          />
        </div>
        <!-- #endregion::Card for main options -->

        <!-- #region::Skeleton zone -->
        <template v-if="loadingDataForm">
          <SaveQuoteFormSkeleton />
        </template>
        <!-- #endregion::Skeleton zone -->

        <!-- #region::Main content -->
        <template v-else>
          <CustomerInformation :customer-data="customerData" />

          <b-row>
            <b-col md="4">
              <InvoiceData
                :customer-data="customerData"
                :quote="quote"
              />
            </b-col>
            <b-col md="4">
              <ShipmentAddress :shipment-address-data="shipmentAddressData" />
            </b-col>
            <b-col md="4">
              <InvoiceAddress :invoice-address-data="invoiceAddressData" />
            </b-col>
          </b-row>
          <b-col md="12">
            <b-tabs>
              <b-tab title="Productos">
                <QuoteProducts
                  :products-data="productsData"
                  class="mt-2 mb-2"
                />
              </b-tab>
              <b-tab title="Circuitos">
                <QuoteCircuits
                  :circuits-data="circuitsData"
                  class="mt-2"
                />
              </b-tab>
            </b-tabs>
          </b-col>

          <ExtraInformation :quote-data="quoteData" />
        </template>
        <!-- #endregion::Main content -->

      </b-form>
      <!-- #endregion::Form -->

    </validation-observer>
  </div>
</template>

<script>
// #region Imports
import { mapActions, mapGetters } from 'vuex'
import { ValidationObserver, localize } from 'vee-validate'
import {
  BForm, BRow, BCol, BTabs, BTab,
} from 'bootstrap-vue'

import MainOptions from '@/components/forms/MainOptions.vue'
import InvoiceData from '@/modules/trade/quotes/components/form/InvoiceData.vue'
import QuoteCircuits from '@/modules/trade/quotes/components/form/QuoteCircuits.vue'
import QuoteProducts from '@/modules/trade/quotes/components/form/QuoteProducts.vue'
import InvoiceAddress from '@/modules/trade/quotes/components/form/InvoiceAddress.vue'
import ShipmentAddress from '@/modules/trade/quotes/components/form/ShipmentAddress.vue'
import ExtraInformation from '@/modules/trade/quotes/components/form/ExtraInformation.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import SaveQuoteFormSkeleton from '@/modules/trade/quotes/components/SaveQuoteFormSkeleton.vue'
import CustomerInformation from '@/modules/trade/quotes/components/form/CustomerInformation.vue'

import getError from '@/helpers/ErrorsHandler'
// #endregion

export default {
  components: {
    BRow,
    BCol,
    BTab,
    BTabs,
    BForm,
    InvoiceData,
    MainOptions,
    QuoteCircuits,
    QuoteProducts,
    InvoiceAddress,
    ShipmentAddress,
    ExtraInformation,
    ValidationObserver,
    CustomerInformation,
    SaveQuoteFormSkeleton,
  },
  props: {
    /**
     * Identificador de la cotización
     */
    id: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      loadingDataForm: true,

      /**
       * Información obtenida desde el servidor
       */
      userData: null,
      quoteData: null,
      circuitsData: null,
      customerData: null,
      productsData: null,
      invoiceAddressData: null,
      shipmentAddressData: null,

      quoteResponse: null,

      savedSuccess: false,
    }
  },
  computed: {
    ...mapGetters({
      getQuoteProducts: 'quotes/getQuoteProducts',
      getQuoteCircuits: 'quotes/getQuoteCircuits',
      getSelectedCustomer: 'quotes/getSelectedCustomer',

      /**
       * Delivery types and methods
       */
      getArriveDate: 'quotes/getArriveDate',
      getCountToInvoice: 'quotes/getCountToInvoice',
      getInvoiceRequired: 'quotes/getInvoiceRequired',
      getSelectedDeliveryType: 'quotes/getSelectedDeliveryType',

      /**
       * Información de los totales de la cotización
       */
      getIVA: 'quotes/getIVA',
      getDiscount: 'quotes/getDiscount',
      getTotalMXN: 'quotes/getTotalMXN',
      getSubtotalUSD: 'quotes/getSubtotalUSD',
      getSubtotalMXN: 'quotes/getSubtotalMXN',
      getExchangeType: 'quotes/getExchangeType',
      getShipmentCost: 'quotes/getShipmentCost',
      getInstallationCost: 'quotes/getInstallationCost',
      getIdInvoiceAddress: 'quotes/getIdInvoiceAddress',
      getIdShipmentAddress: 'quotes/getIdShipmentAddress',

      /**
       * Valida si una cotización es nueva o no
       */
      getQuote: 'quotes/getQuote',
      getIdQuote: 'quotes/getIdQuote',
      getIdCustomer: 'quotes/getIdCustomer',

      /**
       * Lista de circuitos con precio de oferta seleccionado
       */
      getCircuitsWithOfferPriceSelected: 'quotes/getCircuitsWithOfferPriceSelected',
    }),
    /**
     * Lista de productos de la cotización
     */
    quoteProducts: {
      get() { return this.getQuoteProducts },
    },
    /**
     * Lista de circuitos de la cotización
     */
    quoteCircuits: {
      get() { return this.getQuoteCircuits },
    },
    /**
     * Cliente seleccionado para la cotización
     */
    selectedCustomer: {
      get() { return this.getSelectedCustomer },
    },
    /**
     * Fecha de llegada de la cotización
     */
    arriveDate: {
      get() { return this.getArriveDate },
    },
    /**
     * Monto a facturar
     */
    countToInvoice: {
      get() { return this.getCountToInvoice },
    },
    /**
     * Indica si la cotización requiere factura
     */
    invoiceRequired: {
      get() { return this.getInvoiceRequired },
    },
    /**
     * Tipo de entrega seleccionado
     */
    selectedDeliveryType: {
      get() { return this.getSelectedDeliveryType },
    },
    /**
     * Subtotal de la cotización en USD
     */
    subtotalUSD: {
      get() { return this.getSubtotalUSD },
    },
    /**
     * Tipo de cambio de la cotización
     */
    exchangeType: {
      get() { return this.getExchangeType },
    },
    /**
     * Subtotal de la cotización en MXN
     */
    subtotalMXN: {
      get() { return this.getSubtotalMXN },
    },
    /**
     * IVA de la cotización
     */
    IVA: {
      get() { return this.getIVA },
    },
    /**
     * Contiene toda la información de la cotización
     */
    quote: {
      get() { return this.getQuote },
    },
    /**
     * Descuento de la cotización
     */
    discount: {
      get() { return this.getDiscount },
    },
    /**
     * Costo de envío de la cotización
     */
    shipmentCost: {
      get() { return this.getShipmentCost },
    },
    /**
     * Costo de instalación de la cotización
     */
    installationCost: {
      get() { return this.getInstallationCost },
    },
    /**
     * Total de la cotización en MXN
     */
    totalMXN: {
      get() { return this.getTotalMXN },
    },
    /**
     * Identificador de la cotización
     */
    idQuote: {
      get() { return this.getIdQuote },
    },
    /**
     * Identificador del cliente de la cotización
     */
    idCustomer: {
      get() { return this.getIdCustomer },
    },
    /**
     * Identificador de la dirección de envío de la cotización
     */
    idShipmentAddress: {
      get() { return this.getIdShipmentAddress },
    },
    /**
     * Identificador de la dirección de facturación de la cotización
     */
    idInvoiceAddress: {
      get() { return this.getIdInvoiceAddress },
    },
    /**
     * Lista de circuitos con precio de oferta seleccionado
     */
    circuitsWithOfferPriceSelected: {
      get() { return this.getCircuitsWithOfferPriceSelected },
    },
  },

  /**
   * Carga la información de la cotización
   */
  async created() {
    this.$swal({
      title: 'Cargando...',
      allowOutsideClick: false,
    })
    this.$swal.showLoading()
    this.loadingDataForm = true

    // Carga la información de los clientes
    await this.loadCustomers()

    /**
     * Si la cotización existe, carga la información de la misma
     */
    if (this.id) {
      const response = await this.loadQuote(this.id)
      if (response.data.length > 0) {
        // Información de la cotización obtenida desde el servidor
        const [quoteResponse] = response.data
        this.quoteResponse = quoteResponse

        // Información del cliente
        const { client } = quoteResponse
        const [customerData] = client
        this.customerData = customerData

        // Información del usuario
        const { user } = quoteResponse
        const [userData] = user
        this.userData = userData

        // Información de la cotización
        const { quote } = quoteResponse
        const [quoteData] = quote
        this.quoteData = quoteData

        // Información de los productos de la cotización
        const { products } = quoteResponse
        this.productsData = products

        // Información de los circuitos de la cotización
        const { circuits } = quoteResponse
        this.circuitsData = circuits

        // Información de la dirección de facturación
        const { addressBilling } = quoteResponse
        const [invoiceAddressData] = addressBilling
        this.invoiceAddressData = invoiceAddressData

        // Información de la dirección de envío
        const { address } = quoteResponse
        const [shipmentAddressData] = address
        this.shipmentAddressData = shipmentAddressData

        // Establece el identificador de la cotización en el store
        this.setIdQuote(this.id)
      }
    }

    localize('es')
    this.setOriginRouteToCustomerForm('new-quote')

    window.addEventListener('beforeunload', this.leaving)

    this.loadingDataForm = false
    this.$swal.close()
  },
  async beforeDestroy() {
    if (this.idQuote && !this.savedSuccess && !this.id) {
      await this.cancelQuote(this.idQuote)
    }
    this.clearFormData()
  },
  methods: {
    ...mapActions({
      loadQuote: 'quotes/loadQuote',
      setIdQuote: 'quotes/setIdQuote',
      createQuote: 'quotes/createQuote',
      cancelQuote: 'quotes/cancelQuote',
      updateQuote: 'quotes/updateQuote',
      clearFormData: 'quotes/clearFormData',
      loadCustomers: 'quotes/loadCustomers',
      setOriginRouteToCustomerForm: 'customers/setOriginRouteToCustomerForm',
    }),
    leaving() {
      if (!this.id) {
        this.cancelQuote(this.idQuote)
      }
    },
    /**
     * Registra y edita la cotización
     *
     * @summary Si la cotización no existe, la registra. Si la cotización existe, la edita
     * @exception Se muestra un mensaje de error en caso de que la cotización no se
     * haya podido registrar o editar
     */
    async saveQuote() {
      // Obtiene el estado del formulario de la cotización
      const success = await this.$refs.quoteForm.validate()

      // Verifica que todos los productos de los circuitos cuenten con precio unitario
      if (!this.circuitsWithTotal()) {
        this.showToast(
          'Error de validación',
          'Uno o más circuitos cuentan con productos sin precio unitario especificado.',
          'danger',
        )
        return
      }

      // Verifica que exista un cliente seleccionado
      if (this.selectedCustomer) {
        if (!this.selectedCustomer.address[0] && !this.idShipmentAddress) {
          this.showNoAddressMessage()
          return
        }
        if (this.invoiceRequired === 'Si') {
          if (!this.selectedCustomer.billings[0] && !this.idInvoiceAddress) {
            this.showNoBillingsMessage()
            return
          }
        }
      } else if (!this.idShipmentAddress) {
        this.showNoAddressMessage()
        return
      } else if (this.invoiceRequired === 'Si') {
        if (!this.idInvoiceAddress) {
          this.showNoBillingsMessage()
          return
        }
      }

      if (success) {
        this.$swal({
          title: 'Espere por favor',
          allowOutsideClick: false,
        })

        this.$swal.showLoading()

        const formData = new FormData()

        if (this.idCustomer) {
          formData.append('idCliente', this.idCustomer)
        } else if (this.selectedCustomer.user[0].IdUser) {
          formData.append('idCliente', this.selectedCustomer.user[0].IdUser)
        }

        if (this.idShipmentAddress) {
          formData.append('idAddress', this.idShipmentAddress)
        } else if (this.selectedCustomer.address[0]) {
          formData.append('idAddress', this.selectedCustomer.address[0].IdAddress)
        }

        if (this.idInvoiceAddress) {
          formData.append('idBilling', this.idInvoiceAddress)
        } else if (this.selectedCustomer && this.selectedCustomer.billings && this.selectedCustomer.billings[0]) {
          formData.append('idBilling', this.selectedCustomer.billings[0].IdBillingdata)
        }

        this.quoteProducts.forEach(quoteProduct => {
          formData.append('circuits_products[]', this.productData(quoteProduct))
        })

        this.quoteCircuits.forEach(quoteCircuit => {
          quoteCircuit.products.forEach(product => {
            formData.append('circuits_products[]', this.productData(product, quoteCircuit.IdCircuit, quoteCircuit.uuid))
          })
        })

        formData.append('qty', this.piecesCount())
        formData.append('subtotalUsd', this.subtotalUSD)
        formData.append('exchangeRate', this.exchangeType !== '' && this.exchangeType !== null ? this.exchangeType : 0)
        formData.append('subtotalMxn', this.subtotalMXN.toFixed(2))
        formData.append('iva', this.IVA.toFixed(2))
        formData.append('discount', (this.discount !== null && this.dicount !== '') ? this.discount : 0)
        formData.append('shippingCost', this.shipmentCost !== null ? this.shipmentCost : 0)
        formData.append('installationCost', this.installationCost !== null ? this.installationCost : 0)
        formData.append('totalMxn', this.totalMXN)
        formData.append('requiredDeliveryDate', this.arriveDate !== null ? this.arriveDate : '')
        formData.append('deliveryType', this.selectedDeliveryType && this.selectedDeliveryType.text !== null ? this.selectedDeliveryType.text : '')
        formData.append('invoice', this.invoiceRequired)
        formData.append('amountInvoiced', this.invoiceRequired === 'Si' ? this.countToInvoice : 0)

        if (this.id || this.idQuote) {
          formData.append('id', this.id || this.idQuote)
        }

        // eslint-disable-next-line no-restricted-syntax
        for (const pair of formData.entries()) {
          console.log(`${pair[0]}, ${pair[1]}`)
        }

        try {
          if (this.id || this.idQuote) {
            await this.updateQuote(formData)
          } else {
            await this.createQuote(formData)
          }
          this.showSwalFire('¡Los datos de la cotización se han guardado correctamente!')
        } catch (error) {
          this.$swal.close()
          this.showToast('Error de validación', getError(error), 'danger')
        }
      } else {
        this.showToast(
          'Error de validación',
          'Se encontraron uno o más errores en la validación del formulario de la cotización.',
          'danger',
        )
      }
    },

    /**
     * Datos del producto
     *
     * @summary Obtiene los datos de un producto para enviarlos al backend
     * @param {object} product - Información del producto
     * @param {number} idCircuit - Identificador del circuito
     * @returns {object} Retorna los datos del producto en formato JSON
     */
    productData(product, idCircuit = '', uuidCircuit = '') {
      const pieces = []

      product.pieces.forEach(piece => {
        pieces.push({ idPiece: piece.IdPiece })
      })

      const offertedCircuit = this.circuitsWithOfferPriceSelected.find(c => c.uuid === uuidCircuit)

      const productData = JSON.stringify({
        idCircuit,
        idProduct: product.IdProduct,
        unitPrice: offertedCircuit ? '' : product.unitPrice,
        unitPriceOrigin: offertedCircuit ? '' : product.unitPriceOrigin,
        unitPriceType: offertedCircuit ? '' : product.unitPriceType,
        offerPrice: offertedCircuit?.OfferPrice || '',
        methodDelivery: product.DeliveredMethod,
        qty: product.pieces.length,
        pieces,
      })

      return productData
    },

    /**
    * Cantidad de piezas
    *
    * @summary Obtiene la cantidad de piezas de todos los productos y circuitos de la cotización
    * @return {number} Retorna la cantidad de piezas de todos los productos y circuitos de la cotización
    */
    piecesCount() {
      let piecesCount = 0

      this.quoteProducts.forEach(quoteProduct => {
        piecesCount += quoteProduct.pieces.length
      })

      this.quoteCircuits.forEach(quoteCircuit => {
        quoteCircuit.products.forEach(product => {
          piecesCount += product.pieces.length
        })
      })

      return piecesCount
    },

    /**
    * Verifiación del total de los circuitos
    *
    * @summary Verifica si se establecido a todos los productos de los circuitos un precio unitario
    * @return {boolean} Retorna true si todos los productos de los circuitos tienen un precio unitario
    */
    circuitsWithTotal() {
      let withTotal = true

      // Se recorren todos los circuitos de la cotización
      this.quoteCircuits.forEach(quoteCircuit => {
        // Se verifica si el circuito tiene precio de oferta seleccionado
        const offertedCircuit = this.circuitsWithOfferPriceSelected.find(c => c.uuid === quoteCircuit.uuid)
        console.log('offertedCircuit', offertedCircuit)

        // Se omite la verificación de precio unitario si el circuito tiene precio de oferta seleccionado
        if (!offertedCircuit) {
          const hasNotUnitPriceDefined = product => product.unitPrice === 0
          const notUnitPriceDefined = quoteCircuit.products.some(hasNotUnitPriceDefined)

          if (notUnitPriceDefined) {
            withTotal = false
          }
        }
      })

      return withTotal
    },

    /**
    * Ventana de confirmación
    *
    * @summary Muestra una ventana de confirmación cuando se ha registrado una cotizción
    * @param {string} message - Mensaje a mostrar en la ventana de confirmación
    */
    showSwalFire(message) {
      this.$swal.fire({
        title: 'Guardado',
        text: message,
        icon: 'success',
        showCancelButton: false,
        confirmButtonText: 'Continuar',
        allowEscapeKey: false,
        allowOutsideClick: false,
      }).then(result => {
        if (result.isConfirmed) {
          this.savedSuccess = true
          this.$router.push({ name: 'quotes-home' })
        }
      })
    },

    /**
    * Mensaje de validación de la dirección
    *
    * @summary Muestra un mensajde de valicación cuando no se ha seleccionado
    *  una dirección de envío
    */
    showNoAddressMessage() {
      this.showToast(
        'Error de validación',
        'La dirección de envío no ha sido especificada.',
        'danger',
      )
    },

    /**
    * Mensaje de validación de la dirección de facturación
    *
    * @summary Muestra un mensajde de valicación cuando no se ha seleccionado
    *  una dirección de facturación
    */
    showNoBillingsMessage() {
      this.showToast(
        'Error de validación',
        'La dirección de facturación no ha sido especificada.',
        'danger',
      )
    },

    /**
    * Mensaje de validación de los datos de facturación
    *
    * @summary Muestra un mensajde de valicación cuando no se han especificado
    * los datos de facturación
    */
    showNoInvoiceDataMessage() {
      this.showToast(
        'Error de validación',
        'Los datos de facturación no han sido especificados.',
        'danger',
      )
    },

    /**
    * Mensaje de alerta
    *
    * @summary Muestra un mensaje de alerta personalizado
    * @param {string} title - Título del mensaje
    * @param {string} text - Texto del mensaje
    * @param {string} variant - Tipo de mensaje
    */
    showToast(title, text, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title, text, variant, icon: 'BellIcon',
        },
      })
    },
  },
}
</script>
